import React from 'react';
import { Box, Button } from '@drone-js/core';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import { connect } from 'react-redux';
import PageContainer from 'component/common/PageContainer/PageContainer';
import { PageContentStatus } from 'core/type/PageContentStatus';
import StandardErrorPage from 'component/page/error/ActivityStandardErrorPage';
import SearchSupplyError from 'component/page/search/Error/SearchSupplyError';
import SearchPageContent from 'component/page/search/SearchPageContent/SearchPageContent';
import { CityPageProps, mapDispatchToProps, mapStateToProps } from 'component/page/search/City/CitySearchContent/CitySearchContent.selectors';
import { CmsValue, Geo } from 'core/type/BootstrapConfig.types';
import queryActivitiesGqlApi from 'store/api/activitiesGqlApi';
import { ActivityGqlOperation, OperationKey, SearchRequestType } from 'store/api/api.types';
import { getSearchResultNumber } from 'component/page/search/City/CityPageUtils';
import { useLocation } from 'react-router-dom';
import { SearchType } from 'gql/autogen/SearchType';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';

interface SearchContentWrapperProps {
    contentStatus: PageContentStatus;
    pageTypeId: number;
    isNoResults: boolean;
    cms: CmsValue;
    navigateToSsr: () => void;
    isScrolling: boolean;
    isBigScreen: boolean;
    isMobileScreen: boolean;
    geo?: Geo;
}

const SearchContentContentWrapper: React.FC<SearchContentWrapperProps> = React.memo((props) => {
    if (props.contentStatus === PageContentStatus.StandardError) {
        return <StandardErrorPage page="search" contentStatus={props.contentStatus} />;
    }

    if (props.contentStatus === PageContentStatus.SupplyError) {
        return (
            <PageContainer data-testid="activities-search-page" display="flex" alignItems="flex-start" overflowY="visible">
                <SearchSupplyError />
            </PageContainer>
        );
    }

    const embeddedFooter = (
        <Box mt="32" display="flex" justifyContent="center">
            <Button
                data-element-name="activities-see-all-activities"
                width={props.isMobileScreen ? 'parent-fit' : undefined}
                variant="label"
                hierarchy="primary"
                label={props.cms[298606]}
                onClick={props.navigateToSsr}
            />
        </Box>
    );

    return <SearchPageContent isNoResults={props.isNoResults} isEmbedded={true} embeddedFooter={embeddedFooter} />;
});

SearchContentContentWrapper.displayName = 'SearchContentContentWrapper';

const CitySearchContent: React.FC<CityPageProps> = React.memo((props) => {
    const location = useLocation();
    const locationSearch = location.search;
    const isNoResults = props.filteredResultNumber === 0 && !props.isLoading;
    const searchResultNumber = getSearchResultNumber(props.breakpoint);

    React.useEffect(() => {
        // onLocationSearchChange need to be called before queryActivitiesGqlApi because there is setSearchStateFromQueryParams inside.
        props.onLocationSearchChange(locationSearch);
        // The 'searchResultNumber' will change only when resizing the browser. DO not reload the page when user is resizing the browser for better UX.
        queryActivitiesGqlApi({
            args: {
                props: {
                    searchType: SearchType.CITY,
                    searchRequestType: SearchRequestType.DEFAULT,
                    pagination: { number: 1, size: searchResultNumber },
                    defaultSort: { code: SortCode.CityPageSsrSection, order: SortOrder.Descending },
                },
            },
            operation: ActivityGqlOperation.search,
            key: OperationKey.City,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSearch]);

    const navigateToSsr = () => props.geo && props.goToSsrPage(props.geo);

    return (
        <Box mx="4" pb="32">
            <SearchContentContentWrapper
                contentStatus={props.contentStatus}
                pageTypeId={props.pageTypeId}
                isNoResults={isNoResults}
                cms={props.cms}
                geo={props.geo}
                navigateToSsr={navigateToSsr}
                isScrolling={props.isScrolling}
                isBigScreen={props.screenSize.desktop}
                isMobileScreen={props.screenSize.mobile}
            />
        </Box>
    );
});
CitySearchContent.displayName = 'CitySearchContent';

export default withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(CitySearchContent));
